import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import { products, categories } from '../data/products';

const CategoryShowcase = () => {
  const { categoryId } = useParams();
  
  // 获取分类信息
  const category = categories.find(cat => cat.id === categoryId);
  
  // 获取该分类下的所有产品
  const categoryProducts = products.filter(product => product.category === categoryId);
  
  // 获取相关分类（排除当前分类）
  const relatedCategories = categories.filter(cat => cat.id !== categoryId).slice(0, 4);

  if (!category) {
    return (
      <div className="min-h-screen bg-dark-400 pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Category Not Found</h1>
          <Link to="/shop" className="text-primary-400 hover:text-primary-300">
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-400 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* 面包屑导航 */}
        <div className="text-sm text-gray-400 mb-6">
          <Link to="/" className="hover:text-white">Home</Link> / 
          <Link to="/shop" className="hover:text-white mx-2">Shop</Link> / 
          <span className="text-white">{category.name}</span>
        </div>

        {/* 分类头部 */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="mb-6">
            <img 
              src={category.image} 
              alt={category.name} 
              className="w-32 h-32 mx-auto rounded-lg object-cover mb-4"
            />
          </div>
          <h1 className="text-4xl md:text-5xl font-futuristic font-bold mb-4 text-white">
            {category.name}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-6">
            {category.description}
          </p>
          <div className="inline-block bg-primary-500/20 text-primary-400 px-4 py-2 rounded-lg border border-primary-500/30">
            {category.productCount} Products Available
          </div>
        </motion.div>

        {/* 产品网格 */}
        {categoryProducts.length > 0 ? (
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {categoryProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </motion.div>
        ) : (
          <motion.div
            className="text-center py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="text-6xl mb-4">😔</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Products Found</h3>
            <p className="text-gray-400 mb-6">This category is currently empty.</p>
            <Link 
              to="/shop" 
              className="inline-block bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Browse All Products
            </Link>
          </motion.div>
        )}

        {/* 相关分类 */}
        {relatedCategories.length > 0 && (
          <motion.div
            className="mt-20"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Explore Other Categories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedCategories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop/${cat.id}`}
                  className="group block"
                >
                  <motion.div
                    className="card-glass p-6 rounded-lg text-center hover:bg-white/5 transition-all duration-300"
                    whileHover={{ y: -5 }}
                  >
                    <div className="w-20 h-20 mx-auto mb-4 rounded-lg overflow-hidden">
                      <img 
                        src={cat.image} 
                        alt={cat.name} 
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-primary-400 transition-colors">
                      {cat.name}
                    </h3>
                    <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                      {cat.description}
                    </p>
                    <div className="text-primary-400 text-sm font-medium">
                      {cat.productCount} Products
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}

        {/* 返回商店按钮 */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Link
            to="/shop"
            className="inline-block bg-gray-800 hover:bg-gray-700 text-white px-8 py-3 rounded-lg font-medium border border-gray-600 transition-colors"
          >
            ← Back to Shop
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default CategoryShowcase;
